import React, { useState, useEffect } from 'react';
import {
  Button, TextInput, Modal, Tile,
  RadioButtonGroup, RadioButton, Tooltip,
  InlineNotification, Loading
} from '@carbon/react';
import { Demo, TrashCan, CheckmarkFilled } from '@carbon/react/icons';
import { useNavigate } from 'react-router-dom';

const API_ROOT = '/mlapi/premium/mcp-servers';

const CustomApiIntegrationPage = ({ token }) => {
  const nav = useNavigate();

  /* data state */
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);

  /* modal state */
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl]   = useState('');
  const [auth, setAuth] = useState('bearer');
  const [cred, setCred] = useState({ token:'', header:'', key:'', username:'', password:'' });
  const [err,  setErr]  = useState('');

  /* banner {name,count} | null */
  const [banner, setBanner] = useState(null);

  /* fetch servers */
  useEffect(() => {
    fetch(API_ROOT, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r => r.json())
      .then(setServers)
      .catch(console.error)
      .finally(()=>setLoading(false));
  }, [token]);

  const hdr = { 'Content-Type':'application/json', Authorization:`Bearer ${token}` };

  /* add / save */
  const openModal = () => { setName(''); setUrl(''); setAuth('bearer');
    setCred({ token:'', header:'', key:'', username:'', password:'' });
    setErr(''); setOpen(true); };

  const save = () => {
    if (!name || !url) { setErr('Name & URL required'); return; }
    if (auth==='bearer' && !cred.token)           { setErr('Token required'); return; }
    if (auth==='api_key' && (!cred.header||!cred.key)) { setErr('Header & key required'); return; }
    if (auth==='basic'  && (!cred.username||!cred.password)) { setErr('User & pass required'); return; }

    const credentials = auth==='bearer' ? { token:cred.token }
      : auth==='api_key' ? { header:cred.header, key:cred.key }
      : { username:cred.username, password:cred.password };

    fetch(API_ROOT, { method:'POST', headers:hdr,
      body:JSON.stringify({ name, base_url:url, auth_type:auth, credentials }) })
      .then(r=>r.ok?r.json():Promise.reject(r))
      .then(async srv=>{
        setServers(prev=>[...prev,srv]);

        /* pull tools for banner */
        try {
          const t = await fetch(`${API_ROOT}/${srv.id}/tools?refresh=true`,{headers:hdr}).then(r=>r.json());
          setBanner({ name:srv.name, count:t.length });
        } catch {/* ignore */}
        setOpen(false);
      })
      .catch(()=>setErr('Save failed'));
  };

  /* delete */
  const del = id=>{
    if(!window.confirm('Delete this MCP connection?'))return;
    fetch(`${API_ROOT}/${id}`,{method:'DELETE',headers:hdr})
      .then(r=>r.ok&&setServers(servers.filter(s=>s.id!==id)))
      .catch(()=>alert('Delete failed'));
  };

  /* auth-specific fields */
  const CredFields=()=>{
    switch(auth){
      case 'bearer':
        return <TextInput id="tok" labelText="Bearer token"
                 value={cred.token}
                 onChange={e=>setCred({...cred,token:e.target.value})} required />;
      case 'api_key':
        return (<>
          <TextInput id="hdr" labelText="HTTP header name"
            value={cred.header}
            onChange={e=>setCred({...cred,header:e.target.value})} required />
          <TextInput id="key" labelText="Key value"
            value={cred.key}
            onChange={e=>setCred({...cred,key:e.target.value})} required />
        </>);
      case 'basic':
        return (<>
          <TextInput id="usr" labelText="Username"
            value={cred.username}
            onChange={e=>setCred({...cred,username:e.target.value})} required />
          <TextInput id="pwd" labelText="Password" type="password"
            value={cred.password}
            onChange={e=>setCred({...cred,password:e.target.value})} required />
        </>);
      default:return null;
    }
  };

  return (
    <div className="custom-api-integration-page">
      <div className="custom-api-integration-container">

        <h1>MCP Integrations</h1>
        <p>
          Register a Zapier-style MCP server so Fresh&nbsp;Focus&nbsp;AI can
          discover and call personal tools to integrate with your data for your use case.
          &nbsp;&nbsp;&nbsp;
         <Tooltip
            label="Watch a video that shows you exactly how to connect powerful third-party tools in minutes using MCP and see the immediate benefits"
            align="bottom">
            <Button
              kind="ghost"
              size="sm"
              onClick={() =>
                window.open('https://youtu.be/g50i2rDlZks', '_blank', 'noopener')
              }
              className="tooltip-button"
              aria-label="Watch MCP tutorial video">
              <Demo />
            </Button>
          </Tooltip>
        </p>


        {banner && (
          <InlineNotification
            lowContrast
            kind="success"
            title={<span className="flex items-center">
                     <CheckmarkFilled className="mr-2" />
                     {banner.count} tools imported
                   </span>}
            subtitle={`Connection “${banner.name}” added successfully`}
            onClose={()=>setBanner(null)}
            hideCloseButton={false}
            className="success-banner"
          />
        )}

        <Button kind="primary" onClick={openModal}>Add connection</Button>

        {loading ? <Loading active withOverlay={false}/> :
          servers.length ? (
            <div className="api-list">
              {servers.map(s=>(
                <Tile key={s.id} className="api-tile">
                  <h3>{s.name}</h3>
                  <p className="bx--type-caption">{s.base_url}</p>
                  <p className="bx--type-caption">Auth: {s.auth_type}</p>
                  <Button
                    kind="danger--tertiary" size="sm"
                    renderIcon={TrashCan}
                    onClick={()=>del(s.id)}
                  >
                    Delete
                  </Button>
                </Tile>
              ))}
            </div>
          ) : <p>No MCP connections yet.</p>
        }

        {/* modal */}
        <Modal open={open} modalHeading="Add MCP connection"
          primaryButtonText="Save" secondaryButtonText="Cancel"
          onRequestClose={()=>setOpen(false)}
          onRequestSubmit={save}
          size="lg">
          <TextInput id="name" labelText="Name"
            value={name} onChange={e=>setName(e.target.value)} required />
          <TextInput id="url" labelText="Base URL"
            placeholder="https://mcp.zapier.com/api/mcp/mcp"
            value={url} onChange={e=>setUrl(e.target.value)} required />

          <RadioButtonGroup
            legendText="Auth type" name="authType"
            valueSelected={auth}
            onChange={val=>setAuth(val)}
            className="my-4"
          >
            <RadioButton value="bearer" id="bearer" labelText="Bearer token" />
            <RadioButton value="api_key" id="apikey" labelText="API key" />
            <RadioButton value="basic"  id="basic"  labelText="Basic (user & pass)" />
          </RadioButtonGroup>

          <CredFields/>
          {err && <p className="error-message">{err}</p>}
        </Modal>

        <div className="button-group">
          <Button kind="secondary" onClick={()=>nav('/')}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default CustomApiIntegrationPage;
